import React, { useState, useEffect, useCallback } from 'react';
import carecoinsService from '../../services/carecoinsService';
import masterService from '../../services/masterService';
import MasterPageWrapper from './common/MasterPageWrapper';
import FormSection from './common/FormSection';
import CommonPagination from '../../components/common/CommonPagination';
import usePagination from '../../hooks/usePagination';

const INITIAL_FORM = {
  scope: 'GLOBAL', // 'GLOBAL' | 'CENTRE'
  centreId: '',
  isEnabled: false,
  earnRatePercent: '',
  pointValueINR: '',
  minQualifyingBillAmount: '',
  maxRedemptionPercentPerInvoice: '',
  maxRedemptionPointsPerInvoice: '',
  expiryMonths: '',
  allowStackingWithDiscount: true,
  eligibilityMode: 'ALL'
};

// CareCoins — patient loyalty/rewards rule configuration (Admin only).
// Rule versions are insert-only (CARECOINS_DESIGN.md): creating a new version here
// supersedes the previous one for the same scope (global / that centre); nothing is
// ever edited or deleted in place. See docs/carecoins/CARECOINS_DESIGN.md.
export const CareCoinsRulesMasterPage = () => {
  const [viewMode, setViewMode] = useState('list');
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [centres, setCentres] = useState([]);
  const [centreFilter, setCentreFilter] = useState('');

  const [formData, setFormData] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const [successMsg, setSuccessMsg] = useState(null);
  const [errorMsg, setErrorMsg] = useState(null);

  const fetchRules = useCallback(async () => {
    setLoading(true);
    try {
      const res = await carecoinsService.getRules(centreFilter || null);
      if (res.success) {
        setRules(res.data || []);
      } else {
        setErrorMsg(res.message || 'Failed to load CareCoins rule versions.');
      }
    } catch (err) {
      setErrorMsg(err.response?.data?.message || 'Failed to load CareCoins rule versions.');
    } finally {
      setLoading(false);
    }
  }, [centreFilter]);

  useEffect(() => {
    if (viewMode === 'list') fetchRules();
  }, [viewMode, fetchRules]);

  useEffect(() => {
    masterService.getCentres({ isActive: true })
      .then((res) => setCentres(res?.data || []))
      .catch(() => setCentres([]));
  }, []);

  useEffect(() => {
    if (!successMsg && !errorMsg) return;
    const t = setTimeout(() => { setSuccessMsg(null); setErrorMsg(null); }, 4500);
    return () => clearTimeout(t);
  }, [successMsg, errorMsg]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: null }));
  };

  const handleAddNew = () => {
    setFormData(INITIAL_FORM);
    setErrors({});
    setViewMode('form');
  };

  const centreName = (id) => {
    if (id === null || id === undefined) return 'Global (All Centres)';
    const c = centres.find((x) => x.centreId === id);
    return c ? c.centreName : `Centre #${id}`;
  };

  const validate = () => {
    const errs = {};
    if (formData.scope === 'CENTRE' && !formData.centreId) {
      errs.centreId = 'Select a centre, or switch scope to Global.';
    }
    if (formData.earnRatePercent === '' || Number(formData.earnRatePercent) < 0) {
      errs.earnRatePercent = 'Earn rate % is required and cannot be negative.';
    }
    if (formData.pointValueINR === '' || Number(formData.pointValueINR) < 0) {
      errs.pointValueINR = 'Point value (₹) is required and cannot be negative.';
    }
    if (formData.minQualifyingBillAmount === '' || Number(formData.minQualifyingBillAmount) < 0) {
      errs.minQualifyingBillAmount = 'Minimum qualifying bill amount is required and cannot be negative.';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setSaving(true);
    try {
      const payload = {
        centreId: formData.scope === 'CENTRE' ? Number(formData.centreId) : null,
        isEnabled: !!formData.isEnabled,
        earnRatePercent: Number(formData.earnRatePercent),
        pointValueINR: Number(formData.pointValueINR),
        minQualifyingBillAmount: Number(formData.minQualifyingBillAmount),
        maxRedemptionPercentPerInvoice: formData.maxRedemptionPercentPerInvoice === '' ? null : Number(formData.maxRedemptionPercentPerInvoice),
        maxRedemptionPointsPerInvoice: formData.maxRedemptionPointsPerInvoice === '' ? null : Number(formData.maxRedemptionPointsPerInvoice),
        expiryMonths: formData.expiryMonths === '' ? null : Number(formData.expiryMonths),
        allowStackingWithDiscount: !!formData.allowStackingWithDiscount,
        eligibilityMode: formData.eligibilityMode
      };

      const res = await carecoinsService.createRule(payload);
      if (res.success) {
        setSuccessMsg('New CareCoins rule version created — it is now the effective rule for this scope.');
        setViewMode('list');
        fetchRules();
      } else {
        setErrorMsg(res.message || 'Failed to create rule version.');
      }
    } catch (err) {
      setErrorMsg(err.response?.data?.message || 'Failed to create rule version.');
    } finally {
      setSaving(false);
    }
  };

  const {
    page, pageSize, totalRecords, pagedItems: pagedRules, setPage, setPageSize
  } = usePagination({ items: rules, resetDeps: [centreFilter] });

  return (
    <MasterPageWrapper
      masterTitle={viewMode === 'form' ? 'New CareCoins Rule Version' : 'CareCoins — Patient Loyalty / Rewards Rules'}
      masterName="CareCoins Rule"
      viewMode={viewMode}
      onToggleView={(mode) => (mode === 'form' ? handleAddNew() : setViewMode('list'))}
    >
      {(successMsg || errorMsg) && (
        <div
          className="fade-in"
          style={{
            marginBottom: '1rem',
            padding: '0.7rem 1rem',
            borderRadius: '8px',
            fontSize: '0.88rem',
            fontWeight: 500,
            background: successMsg ? '#ecfdf5' : '#fef2f2',
            color: successMsg ? '#047857' : '#b91c1c',
            border: `1px solid ${successMsg ? '#a7f3d0' : '#fecaca'}`
          }}
        >
          {successMsg || errorMsg}
        </div>
      )}

      {/* ─── FORM VIEW: create a new rule version (insert-only) ─── */}
      {viewMode === 'form' && (
        <div className="glass-card fade-in">
          <form onSubmit={handleSubmit} noValidate>
            <FormSection title="01. Scope" iconColor="#4287f5">
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">Applies To</label>
                  <select
                    className="form-select"
                    value={formData.scope}
                    onChange={(e) => handleChange('scope', e.target.value)}
                  >
                    <option value="GLOBAL">Global (All Centres)</option>
                    <option value="CENTRE">Specific Centre</option>
                  </select>
                </div>

                {formData.scope === 'CENTRE' && (
                  <div className="form-group">
                    <label className="form-label">Centre <span className="form-req">*</span></label>
                    <select
                      className={`form-select ${errors.centreId ? 'border-danger' : ''}`}
                      value={formData.centreId}
                      onChange={(e) => handleChange('centreId', e.target.value)}
                    >
                      <option value="">Select centre...</option>
                      {centres.map((c) => (
                        <option key={c.centreId} value={c.centreId}>{c.centreName}</option>
                      ))}
                    </select>
                    {errors.centreId && <span className="error-message">{errors.centreId}</span>}
                  </div>
                )}
              </div>

              <label className="checkbox-label" style={{ marginTop: '0.75rem' }}>
                <input
                  type="checkbox"
                  checked={formData.isEnabled}
                  onChange={(e) => handleChange('isEnabled', e.target.checked)}
                />
                <span>
                  <strong>Enable this rule</strong> — when on, this becomes the live, money-value
                  earning/redemption rule for this scope as soon as it's saved. Leave unchecked to
                  save a disabled draft version.
                </span>
              </label>
            </FormSection>

            <FormSection title="02. Earning" iconColor="#10b981">
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">Earn Rate (%) <span className="form-req">*</span></label>
                  <input
                    type="number" step="0.01" min="0"
                    className={`form-input ${errors.earnRatePercent ? 'border-danger' : ''}`}
                    value={formData.earnRatePercent}
                    onChange={(e) => handleChange('earnRatePercent', e.target.value)}
                    placeholder="e.g. 2.00 (2% of bill value as points)"
                  />
                  {errors.earnRatePercent && <span className="error-message">{errors.earnRatePercent}</span>}
                </div>

                <div className="form-group">
                  <label className="form-label">Minimum Qualifying Bill (₹) <span className="form-req">*</span></label>
                  <input
                    type="number" step="0.01" min="0"
                    className={`form-input ${errors.minQualifyingBillAmount ? 'border-danger' : ''}`}
                    value={formData.minQualifyingBillAmount}
                    onChange={(e) => handleChange('minQualifyingBillAmount', e.target.value)}
                    placeholder="e.g. 500 (no points below this bill value)"
                  />
                  {errors.minQualifyingBillAmount && <span className="error-message">{errors.minQualifyingBillAmount}</span>}
                </div>

                <div className="form-group">
                  <label className="form-label">Expiry (Months)</label>
                  <input
                    type="number" step="1" min="0"
                    className="form-input"
                    value={formData.expiryMonths}
                    onChange={(e) => handleChange('expiryMonths', e.target.value)}
                    placeholder="Leave blank — points never expire"
                  />
                </div>
              </div>
            </FormSection>

            <FormSection title="03. Redemption" iconColor="#f59e0b">
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">Point Value (₹ per point) <span className="form-req">*</span></label>
                  <input
                    type="number" step="0.0001" min="0"
                    className={`form-input ${errors.pointValueINR ? 'border-danger' : ''}`}
                    value={formData.pointValueINR}
                    onChange={(e) => handleChange('pointValueINR', e.target.value)}
                    placeholder="e.g. 1.00 (1 point = ₹1 discount)"
                  />
                  {errors.pointValueINR && <span className="error-message">{errors.pointValueINR}</span>}
                </div>

                <div className="form-group">
                  <label className="form-label">Max Redemption (% of invoice)</label>
                  <input
                    type="number" step="0.01" min="0"
                    className="form-input"
                    value={formData.maxRedemptionPercentPerInvoice}
                    onChange={(e) => handleChange('maxRedemptionPercentPerInvoice', e.target.value)}
                    placeholder="Leave blank — no % cap"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Max Redemption (points per invoice)</label>
                  <input
                    type="number" step="1" min="0"
                    className="form-input"
                    value={formData.maxRedemptionPointsPerInvoice}
                    onChange={(e) => handleChange('maxRedemptionPointsPerInvoice', e.target.value)}
                    placeholder="Leave blank — no points cap"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Eligibility Mode</label>
                  <select
                    className="form-select"
                    value={formData.eligibilityMode}
                    onChange={(e) => handleChange('eligibilityMode', e.target.value)}
                  >
                    <option value="ALL">All Tests / Packages</option>
                    <option value="INCLUDE_LIST">Include List Only</option>
                    <option value="EXCLUDE_LIST">Exclude List</option>
                  </select>
                  {formData.eligibilityMode !== 'ALL' && (
                    <span className="error-message" style={{ color: 'var(--text-muted)' }}>
                      Note: there is no screen yet to manage the include/exclude test list for this
                      rule version — until that's built, INCLUDE_LIST behaves as "nothing eligible"
                      and EXCLUDE_LIST behaves as "everything eligible". Use "All Tests / Packages"
                      unless you know this is being configured separately.
                    </span>
                  )}
                </div>
              </div>

              <label className="checkbox-label" style={{ marginTop: '0.75rem' }}>
                <input
                  type="checkbox"
                  checked={formData.allowStackingWithDiscount}
                  onChange={(e) => handleChange('allowStackingWithDiscount', e.target.checked)}
                />
                <span>Allow combining CareCoins redemption with a manual discount on the same bill</span>
              </label>
            </FormSection>

            <div className="form-actions-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setViewMode('list')} disabled={saving}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary" disabled={saving} style={{ minWidth: '160px' }}>
                {saving ? 'Saving...' : 'Save New Rule Version'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* ─── LIST VIEW: read-only history of all rule versions ─── */}
      {viewMode === 'list' && (
        <div className="glass-card fade-in" style={{ padding: '1.5rem' }}>
          <div className="filter-bar">
            <div className="filter-bar-left">
              <select
                className="form-select"
                style={{ width: '220px' }}
                value={centreFilter}
                onChange={(e) => { setCentreFilter(e.target.value); setPage(1); }}
              >
                <option value="">All Scopes (Global + Centres)</option>
                {centres.map((c) => (
                  <option key={c.centreId} value={c.centreId}>{c.centreName}</option>
                ))}
              </select>
            </div>
            <div className="filter-bar-right">
              <button type="button" className="btn btn-primary" onClick={handleAddNew}>
                + New Rule Version
              </button>
            </div>
          </div>

          <div className="table-responsive">
            <table className="master-table mvc-table">
              <thead>
                <tr>
                  <th style={{ width: '60px', textAlign: 'center' }}>Ver.</th>
                  <th style={{ width: '170px' }}>Scope</th>
                  <th style={{ width: '90px', textAlign: 'center' }}>Status</th>
                  <th style={{ width: '100px' }}>Earn %</th>
                  <th style={{ width: '110px' }}>Point Value</th>
                  <th style={{ width: '110px' }}>Min Bill</th>
                  <th style={{ width: '140px' }}>Max Redemption</th>
                  <th style={{ width: '90px' }}>Expiry</th>
                  <th style={{ width: '90px', textAlign: 'center' }}>Stacking</th>
                  <th style={{ width: '120px' }}>Eligibility</th>
                  <th style={{ width: '150px' }}>Effective From</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan="11" style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-muted)' }}>Loading rule versions...</td></tr>
                ) : rules.length === 0 ? (
                  <tr><td colSpan="11" style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-muted)' }}>No CareCoins rule versions configured yet.</td></tr>
                ) : (
                  pagedRules.map((r) => (
                    <tr key={r.ruleVersionId}>
                      <td style={{ textAlign: 'center', fontFamily: 'monospace', color: 'var(--text-muted)' }}>#{r.ruleVersionId}</td>
                      <td style={{ fontWeight: 600 }}>{centreName(r.centreId)}</td>
                      <td style={{ textAlign: 'center' }}>
                        <span className={`badge-status ${r.isEnabled ? 'active' : 'inactive'}`}>
                          {r.isEnabled ? 'Enabled' : 'Disabled'}
                        </span>
                      </td>
                      <td>{Number(r.earnRatePercent).toFixed(2)}%</td>
                      <td>₹{Number(r.pointValueINR).toFixed(4)}</td>
                      <td>₹{Number(r.minQualifyingBillAmount).toFixed(2)}</td>
                      <td style={{ fontSize: '0.82rem' }}>
                        {r.maxRedemptionPercentPerInvoice ? `${r.maxRedemptionPercentPerInvoice}%` : '—'}
                        {r.maxRedemptionPointsPerInvoice ? ` / ${r.maxRedemptionPointsPerInvoice} pts` : ''}
                      </td>
                      <td>{r.expiryMonths ? `${r.expiryMonths} mo` : 'Never'}</td>
                      <td style={{ textAlign: 'center' }}>{r.allowStackingWithDiscount ? 'Yes' : 'No'}</td>
                      <td style={{ fontSize: '0.82rem' }}>{r.eligibilityMode}</td>
                      <td style={{ fontSize: '0.82rem' }}>{r.effectiveFrom ? new Date(r.effectiveFrom).toLocaleString() : '—'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <CommonPagination
            page={page}
            pageSize={pageSize}
            totalRecords={totalRecords}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
            loading={loading}
          />
        </div>
      )}
    </MasterPageWrapper>
  );
};

export default CareCoinsRulesMasterPage;
